import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Text,
  Textarea,
  VStack,
} from '@enechain/ecloud-designsystem'

import {
  FieldErrorText,
  FieldLabel,
  FieldRoot,
} from '~/components/common/Field'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { MEMO_MAX_LENGTH, memoLength, truncateMemo } from '~/models/memo'

type Props = {
  isOpen: boolean
  title: string
  context: React.ReactNode
  initialMemo: string | undefined
  textareaLabel: string
  placeholder: string
  saveLabel: string
  isPending: boolean
  errorMessage?: string
  mapError?: (err: unknown) => string | undefined
  onClose: () => void
  onSave: (memo: string) => Promise<void>
}

/**
 * メモ追加・編集の汎用ダイアログ。100 文字制限・空白トリム前のカウント表示を共通化。
 * 新規追加で空のまま保存した場合は何もせず閉じる。既存メモを空にして保存した場合は
 * 呼び出し側の onSave で削除経路を実行する。
 */
export const MemoDialog = ({
  isOpen,
  title,
  context,
  initialMemo,
  textareaLabel,
  placeholder,
  saveLabel,
  isPending,
  errorMessage,
  mapError,
  onClose,
  onSave,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [draftMemo, setDraftMemo] = useState(initialMemo ?? '')
  const [saveError, setSaveError] = useState<string | undefined>(undefined)

  const isEditing = initialMemo !== undefined
  const draftLength = memoLength(draftMemo)

  const displayError = errorMessage ?? saveError

  const handleSave = async (): Promise<void> => {
    setSaveError(undefined)
    if (draftMemo.trim() === '' && !isEditing) {
      onClose()
      return
    }
    try {
      await onSave(draftMemo)
      onClose()
    } catch (err) {
      setSaveError(
        mapError?.(err) ?? t('page:workflowCaseDetail.error.unknown'),
      )
    }
  }

  return (
    <Modal
      footer={
        <SaveCancelFooter
          isPending={isPending}
          isSaveDisabled={draftLength > MEMO_MAX_LENGTH}
          onCancel={onClose}
          onSave={() => {
            void handleSave()
          }}
          saveLabel={saveLabel}
        />
      }
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      title={title}
    >
      <VStack align="stretch" gap={3}>
        {displayError !== undefined && (
          <Alert status="error" title={displayError} />
        )}
        <Box
          padding="8px 12px"
          background="Surface.secondary"
          borderRadius="base"
        >
          {context}
        </Box>
        {/* 入力欄は **DS の form field 構成**に載せる (2026-09-28 デザイナー指摘
            「textarea が DS 違反」)。 以前はラベルが素の `Text`、 不備表示は
            カウンタ文字列を赤くするだけで、 入力欄には枠も `aria-invalid` も
            付いていなかった。 同じ画面のサブステップメモ (`SubStepMemoDialog`) は
            2026-09-07 に是正済みで、 承認メモ / 完了メモ だけ取り残されていた。

            `FieldRoot invalid` が配下の `Textarea` へ `aria-invalid` と赤枠を、
            `FieldErrorText` がメッセージを出す。 `invalid` は **上限に到達した
            時点から**立てる (DS `TextArea` の状態は Default / Disabled / Invalid の
            3 つで warning が無く、「赤い注意文字だけで枠は通常」 は DS に無い)。 */}
        <FieldRoot invalid={draftLength >= MEMO_MAX_LENGTH}>
          <FieldLabel>{textareaLabel}</FieldLabel>
          <Textarea
            aria-label={textareaLabel}
            onChange={(e) => {
              const value = e.target.value
              setDraftMemo((prev) => {
                if (memoLength(value) <= MEMO_MAX_LENGTH) {
                  return value
                }
                // 上限超過時、文字数が増えた（貼り付け・入力）ときだけ切り詰める。
                // 文字数が減る操作（削除・バックスペース）ではそのまま減らせるようにする。
                return memoLength(value) > memoLength(prev)
                  ? truncateMemo(value, MEMO_MAX_LENGTH)
                  : value
              })
            }}
            placeholder={placeholder}
            rows={4}
            value={draftMemo}
          />
          {/* カウンタは件数だけを持つ。 理由の文言 (上限に達した / 超えている) は
              DS の error スロット (`FieldErrorText`) が唯一の置き場所で、
              両方に書くと同じことを 2 回読ませることになる。 */}
          <Text
            color={
              draftLength >= MEMO_MAX_LENGTH
                ? 'Text.negativeMid'
                : 'Text.lowEmphasis'
            }
            fontSize="xs"
            textAlign="right"
          >
            {t('page:workflowCaseDetail.subStep.memo.counter', {
              count: draftLength,
              max: MEMO_MAX_LENGTH,
            })}
          </Text>
          {/* 到達と超過で文言を変える。 ちょうど上限文字数に「上限を超えて
              います」 と出すと事実と食い違う。 */}
          <FieldErrorText>
            {draftLength > MEMO_MAX_LENGTH
              ? t('page:workflowCaseDetail.subStep.memo.overLimit', {
                  max: MEMO_MAX_LENGTH,
                })
              : t('page:workflowCaseDetail.subStep.memo.counterMax')}
          </FieldErrorText>
        </FieldRoot>
      </VStack>
    </Modal>
  )
}
